<template>
  <MapContour
    v-if="userLocation && options"
    :options="options"
    @load="loadMap"
    :sources="sources"
    :layers="layers"
    :marker="true"
    @markerMove="$emit('markerMove', $event)"
  />
</template>
<script setup>
import { bbox, featureCollection, point } from "@turf/turf";
const emit = defineEmits(["markerMove"]);
const props = defineProps({
  location: {
    type: Object,
  },
  readings: {
    type: Array,
    required: true,
  },
  options: {
    type: Object,
    default: {},
  },
});
let map;

function getLabel(reading) {
  let labels = {};
  if ("snowfall_inches" in reading) {
    labels.snowfall_label = `${round(reading.snowfall_inches, 1)} in snowfall\n`;
  }
  if ("snow_depth_inches" in reading) {
    labels.snow_depth_label = `${round(reading.snow_depth_inches, 1)} in snow depth\n`;
  }
  return labels;
}
const userLocation = computed(() => {
  if (isNumber(props.location?.latitude) && isNumber(props.location?.longitude)) {
    return point([props.location.longitude, props.location.latitude], {
      type: "userLocation",
    });
  }
  return null;
});

const geojson = computed(() => {
  if (props.readings.length < 1) {
    return featureCollection([]);
  }

  const features = [];
  props.readings.forEach((reading) => {
    if ("snow_depth_inches" in reading || "snowfall_inches" in reading) {
      features.push(
        point([reading.longitude, reading.latitude], {
          ...reading,
          ...getLabel(reading),
        })
      );
    }
  });
  return featureCollection(features);
});

const sources = computed(() => [
  {
    id: "overlay",
    type: "geojson",
    data: geojson.value,
  },
]);

const options = computed(() => {
  const output = {
    center: userLocation.value.geometry.coordinates,
    fitBoundsOptions: { padding: 50 },
    ...props.options,
  };
  if (geojson.value.features.length > 0) {
    output.bounds = bbox(geojson.value);
  } else {
    output.zoom = 9;
  }
  return output;
});
const layers = computed(() => [
  {
    id: "overlay-points",
    type: "circle",
    source: "overlay",
    paint: {
      // "circle-radius": 6,
      "circle-radius": ["interpolate", ["linear"], ["get", "snow_depth_inches"], 0, 4, 100, 30, 1000, 40],
      "circle-color": "#1e86e9",
      "circle-opacity": 0.5,
    },
  },
  {
    id: "overlay-labels",
    type: "symbol",
    source: "overlay",
    layout: {
      "text-font": ["Noto Sans Bold"],
      "text-field": [
        "format",
        ["get", "snow_depth_label"],
        { "font-scale": 1.1 },
        "",
        {},
        ["get", "snowfall_label"],
        {
          "font-scale": 1.1,
          "text-font": ["literal", ["Noto Sans Regular"]],
        },
      ],
      "text-size": 14,
      "symbol-spacing": 1,
      "text-anchor": "bottom",
      "text-radial-offset": 1,
      "text-variable-anchor": ["top", "bottom"],
    },
    paint: {
      "text-color": "#1e86e9",
      "text-halo-color": "#fff",
      "text-halo-width": 1,
    },
  },
]);

function loadMap(mapValue) {
  map = mapValue;
  map.setFilter("overlay-points", ["!=", "type", "userLocation"]);
}
</script>
